import {
  BusinessIdea,
  BusinessIdeaKit,
  RevenueModel,
  MonetizationStrategy,
  MarketAnalysis,
  Competitor,
  ResourceRequirements,
  SkillRequirement,
  ToolRequirement,
  CostEstimate,
  LaunchGuide,
  LaunchPhase,
  SurveyQuestion,
  TaskItem
} from '../../types/ai';

/**
 * Response Parser Service
 * Handles parsing and validation of AI responses
 */
export class ResponseParser {
  /**
   * Parse business idea kits from AI response
   * Handles enhanced business ideas with revenue models, market analysis, resources, and launch guides
   */
  static parseBusinessIdeaKit(response: string): BusinessIdeaKit[] {
    try {
      // Extract JSON from markdown code blocks or raw response
      let jsonContent = this.extractJSON(response);

      // Try to repair common JSON issues
      jsonContent = this.repairJSON(jsonContent);

      const ideas = JSON.parse(jsonContent);

      if (!Array.isArray(ideas) || ideas.length === 0) {
        throw new Error('Invalid ideas format');
      }

      return ideas.map((idea, index) => this.parseBusinessIdeaKitItem(idea, index));
    } catch (error) {
      console.error('Failed to parse business idea kits:', error);
      console.error('Response excerpt:', response.substring(0, 500));
      throw new Error('Failed to parse AI response');
    }
  }

  /**
   * Parse a single business idea kit item with graceful degradation
   */
  private static parseBusinessIdeaKitItem(idea: any, index: number): BusinessIdeaKit {
    // Parse core BusinessIdea fields (required)
    const baseIdea: BusinessIdea = {
      title: idea.title || `Generated Idea ${index + 1}`,
      problem: idea.problem || 'Problem description not provided',
      solution: idea.solution || 'Solution description not provided',
      targetAudience: idea.targetAudience || idea.targetCustomer || 'Target audience not specified',
      targetCustomer: idea.targetCustomer || idea.targetAudience || 'Target customer not specified',
      validationQuestion: idea.validationQuestion || 'Would you use this solution?',
      skillsRequired: this.parseSkillsArray(idea.skillsRequired),
      technicalFeasibility: this.validateFeasibility(idea.technicalFeasibility),
      estimatedDevelopment: idea.estimatedDevelopment || '2-4 weeks'
    };

    // Parse enhancement fields with graceful degradation
    const revenueModel = this.parseRevenueModel(idea.revenueModel);
    const marketAnalysis = this.parseMarketAnalysis(idea.marketAnalysis);
    const resourceRequirements = this.parseResourceRequirements(idea.resourceRequirements);
    const launchGuide = this.parseLaunchGuide(idea.launchGuide);

    return {
      ...baseIdea,
      revenueModel,
      marketAnalysis,
      resourceRequirements,
      launchGuide
    };
  }

  /**
   * Parse revenue model with validation
   */
  private static parseRevenueModel(data: any): RevenueModel {
    if (!data || typeof data !== 'object') {
      return this.getDefaultRevenueModel();
    }

    const strategies = Array.isArray(data.strategies)
      ? data.strategies.map((s: any) => this.parseMonetizationStrategy(s)).filter(Boolean)
      : [];

    if (strategies.length === 0) {
      return this.getDefaultRevenueModel();
    }

    return {
      strategies,
      estimatedRevenue: data.estimatedRevenue || undefined
    };
  }

  /**
   * Parse monetization strategy
   */
  private static parseMonetizationStrategy(data: any): MonetizationStrategy | null {
    if (!data || typeof data !== 'object') {
      return null;
    }

    if (!data.type || !data.description) {
      return null;
    }

    return {
      type: String(data.type),
      description: String(data.description),
      pricingExample: data.pricingExample ? String(data.pricingExample) : 'Pricing to be determined',
      targetSegment: data.targetSegment ? String(data.targetSegment) : 'General market'
    };
  }

  /**
   * Parse market analysis with validation
   */
  private static parseMarketAnalysis(data: any): MarketAnalysis {
    if (!data || typeof data !== 'object') {
      return this.getDefaultMarketAnalysis();
    }

    const competitors = Array.isArray(data.competitors)
      ? data.competitors.map((c: any) => this.parseCompetitor(c)).filter(Boolean)
      : [];

    const opportunityGaps = Array.isArray(data.opportunityGaps)
      ? data.opportunityGaps.filter((gap: any) => typeof gap === 'string' && gap.trim().length > 0)
      : [];

    return {
      marketSize: data.marketSize ? String(data.marketSize) : 'Market size to be determined',
      competitors: competitors.length > 0 ? competitors : this.getDefaultMarketAnalysis().competitors,
      opportunityGaps: opportunityGaps.length > 0 ? opportunityGaps : this.getDefaultMarketAnalysis().opportunityGaps
    };
  }

  /**
   * Parse competitor information
   */
  private static parseCompetitor(data: any): Competitor | null {
    if (!data || typeof data !== 'object' || !data.name) {
      return null;
    }

    return {
      name: String(data.name),
      strengths: Array.isArray(data.strengths)
        ? data.strengths.filter((s: any) => typeof s === 'string' && s.trim().length > 0)
        : [],
      weaknesses: Array.isArray(data.weaknesses)
        ? data.weaknesses.filter((w: any) => typeof w === 'string' && w.trim().length > 0)
        : []
    };
  }

  /**
   * Parse resource requirements with validation
   */
  private static parseResourceRequirements(data: any): ResourceRequirements {
    if (!data || typeof data !== 'object') {
      return this.getDefaultResourceRequirements();
    }

    const skills = Array.isArray(data.skills)
      ? data.skills.map((s: any) => this.parseSkillRequirement(s)).filter(Boolean)
      : [];

    const tools = Array.isArray(data.tools)
      ? data.tools.map((t: any) => this.parseToolRequirement(t)).filter(Boolean)
      : [];

    const estimatedCost = this.parseCostEstimate(data.estimatedCost);

    return {
      skills: skills.length > 0 ? skills : this.getDefaultResourceRequirements().skills,
      tools: tools.length > 0 ? tools : this.getDefaultResourceRequirements().tools,
      estimatedCost
    };
  }

  /**
   * Parse skill requirement
   */
  private static parseSkillRequirement(data: any): SkillRequirement | null {
    if (!data || typeof data !== 'object' || !data.skill) {
      return null;
    }

    return {
      skill: String(data.skill),
      level: this.validateSkillLevel(data.level),
      canLearn: data.canLearn === true || data.canLearn === false ? data.canLearn : true
    };
  }

  /**
   * Parse tool requirement
   */
  private static parseToolRequirement(data: any): ToolRequirement | null {
    if (!data || typeof data !== 'object' || !data.name) {
      return null;
    }

    return {
      name: String(data.name),
      purpose: data.purpose ? String(data.purpose) : 'Tool purpose not specified',
      cost: data.cost ? String(data.cost) : 'Cost to be determined',
      alternatives: Array.isArray(data.alternatives)
        ? data.alternatives.filter((a: any) => typeof a === 'string' && a.trim().length > 0)
        : []
    };
  }

  /**
   * Parse cost estimate
   */
  private static parseCostEstimate(data: any): CostEstimate {
    if (!data || typeof data !== 'object') {
      return this.getDefaultCostEstimate();
    }

    const breakdown = Array.isArray(data.breakdown)
      ? data.breakdown
        .filter((b: any) => b && typeof b === 'object' && b.category && b.amount)
        .map((b: any) => ({
          category: String(b.category),
          amount: String(b.amount)
        }))
      : [];

    return {
      minimum: data.minimum ? String(data.minimum) : '$0-500',
      recommended: data.recommended ? String(data.recommended) : '$500-2000',
      breakdown: breakdown.length > 0 ? breakdown : this.getDefaultCostEstimate().breakdown
    };
  }

  /**
   * Parse launch guide with validation
   */
  private static parseLaunchGuide(data: any): LaunchGuide {
    if (!data || typeof data !== 'object') {
      return this.getDefaultLaunchGuide();
    }

    const phases = Array.isArray(data.phases)
      ? data.phases.map((p: any) => this.parseLaunchPhase(p)).filter(Boolean)
      : [];

    return {
      phases: phases.length > 0 ? phases : this.getDefaultLaunchGuide().phases,
      totalTimeline: data.totalTimeline ? String(data.totalTimeline) : '3-6 months to first revenue'
    };
  }

  /**
   * Parse launch phase
   */
  private static parseLaunchPhase(data: any): LaunchPhase | null {
    if (!data || typeof data !== 'object' || !data.name) {
      return null;
    }

    return {
      phase: typeof data.phase === 'number' ? data.phase : 1,
      name: String(data.name),
      duration: data.duration ? String(data.duration) : '2-4 weeks',
      milestones: Array.isArray(data.milestones)
        ? data.milestones.filter((m: any) => typeof m === 'string' && m.trim().length > 0)
        : [],
      actions: Array.isArray(data.actions)
        ? data.actions.filter((a: any) => typeof a === 'string' && a.trim().length > 0)
        : []
    };
  }

  /**
   * Extract JSON from markdown code blocks or raw response
   */
  private static extractJSON(response: string): string {
    // Try to extract from markdown code block first
    const codeBlockMatch = response.match(/```(?:json)?\s*(\[[\s\S]*?\])\s*```/);
    if (codeBlockMatch) {
      return codeBlockMatch[1];
    }

    // Try to find JSON array in response
    const jsonMatch = response.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      return jsonMatch[0];
    }

    throw new Error('No JSON array found in response');
  }

  /**
   * Repair common JSON syntax errors
   */
  private static repairJSON(jsonStr: string): string {
    let repaired = jsonStr;

    // Remove any control characters that might break JSON
    repaired = repaired.replace(/[\x00-\x1F\x7F]/g, '');

    // Remove trailing commas before closing brackets/braces
    repaired = repaired.replace(/,(\s*[}\]])/g, '$1');

    // Fix missing commas between array elements (common AI error)
    // Look for } followed by { without comma (with optional whitespace/newlines)
    repaired = repaired.replace(/}(\s*){/g, '},$1{');

    // Fix missing commas between object properties
    // Look for " followed by newline and " without comma
    repaired = repaired.replace(/"(\s*\n\s*)"([^"]+)":/g, '",\n"$2":');

    // Try to fix incomplete JSON by adding missing closing brackets/braces
    // Count opening and closing brackets/braces
    const openBrackets = (repaired.match(/\[/g) || []).length;
    const closeBrackets = (repaired.match(/\]/g) || []).length;
    const openBraces = (repaired.match(/\{/g) || []).length;
    const closeBraces = (repaired.match(/\}/g) || []).length;

    // If we have more opening than closing, try to close them
    // But be conservative - only add if the difference is reasonable
    if (openBraces > closeBraces && openBraces - closeBraces <= 10) {
      for (let i = 0; i < openBraces - closeBraces; i++) {
        repaired += '}';
      }
    }

    if (openBrackets > closeBrackets && openBrackets - closeBrackets <= 5) {
      for (let i = 0; i < openBrackets - closeBrackets; i++) {
        repaired += ']';
      }
    }

    return repaired;
  }

  /**
   * Parse skills array from various formats
   */
  private static parseSkillsArray(skills: any): string[] {
    if (Array.isArray(skills)) {
      return skills.filter((skill: any) => typeof skill === 'string' && skill.trim().length > 0);
    }
    if (typeof skills === 'string' && skills.trim().length > 0) {
      return [skills.trim()];
    }
    return [];
  }

  /**
   * Validate skill level enum
   */
  private static validateSkillLevel(level: any): 'beginner' | 'intermediate' | 'advanced' {
    const normalized = String(level).toLowerCase();
    if (['beginner', 'intermediate', 'advanced'].includes(normalized)) {
      return normalized as 'beginner' | 'intermediate' | 'advanced';
    }
    return 'intermediate';
  }

  /**
   * Default revenue model for graceful degradation
   */
  private static getDefaultRevenueModel(): RevenueModel {
    return {
      strategies: [
        {
          type: 'Subscription',
          description: 'Monthly or annual subscription model',
          pricingExample: 'Pricing to be determined based on market research',
          targetSegment: 'Target customers to be identified'
        }
      ],
      estimatedRevenue: 'Revenue projections to be calculated'
    };
  }

  /**
   * Default market analysis for graceful degradation
   */
  private static getDefaultMarketAnalysis(): MarketAnalysis {
    return {
      marketSize: 'Market size analysis required',
      competitors: [
        {
          name: 'Competitor analysis required',
          strengths: ['Market research needed'],
          weaknesses: ['Opportunity analysis needed']
        }
      ],
      opportunityGaps: ['Market opportunity analysis required']
    };
  }

  /**
   * Default resource requirements for graceful degradation
   */
  private static getDefaultResourceRequirements(): ResourceRequirements {
    return {
      skills: [
        {
          skill: 'Business planning',
          level: 'beginner',
          canLearn: true
        }
      ],
      tools: [
        {
          name: 'Basic tools',
          purpose: 'Essential business operations',
          cost: 'To be determined',
          alternatives: []
        }
      ],
      estimatedCost: this.getDefaultCostEstimate()
    };
  }

  /**
   * Default cost estimate for graceful degradation
   */
  private static getDefaultCostEstimate(): CostEstimate {
    return {
      minimum: '$0-500',
      recommended: '$500-2000',
      breakdown: [
        { category: 'Initial setup', amount: '$0-500' },
        { category: 'Tools and services', amount: '$0-1000' },
        { category: 'Marketing', amount: '$0-500' }
      ]
    };
  }

  /**
   * Default launch guide for graceful degradation
   */
  private static getDefaultLaunchGuide(): LaunchGuide {
    return {
      phases: [
        {
          phase: 1,
          name: 'Planning',
          duration: '1-2 weeks',
          milestones: ['Define business model', 'Identify target market'],
          actions: ['Research competitors', 'Validate idea', 'Create business plan']
        },
        {
          phase: 2,
          name: 'Development',
          duration: '4-8 weeks',
          milestones: ['Build MVP', 'Test with users'],
          actions: ['Develop core features', 'Gather feedback', 'Iterate on design']
        },
        {
          phase: 3,
          name: 'Launch',
          duration: '2-4 weeks',
          milestones: ['Public launch', 'First customers'],
          actions: ['Marketing campaign', 'Onboard users', 'Monitor metrics']
        }
      ],
      totalTimeline: '3-6 months to first revenue'
    };
  }

  /**
   * Parse business ideas from AI response
   */
  static parseBusinessIdeas(response: string): BusinessIdea[] {
    try {
      const jsonMatch = response.match(/\[[\s\S]*\]/);
      if (!jsonMatch) {
        throw new Error('No JSON array found in response');
      }

      // Repair JSON before parsing
      const repairedJSON = this.repairJSON(jsonMatch[0]);
      const ideas = JSON.parse(repairedJSON);

      if (!Array.isArray(ideas) || ideas.length === 0) {
        throw new Error('Invalid ideas format');
      }

      return ideas.map((idea, index) => ({
        title: idea.title || `Generated Idea ${index + 1}`,
        problem: idea.problem || 'Problem description not provided',
        solution: idea.solution || 'Solution description not provided',
        targetAudience: idea.targetAudience || idea.targetCustomer || 'Target audience not specified',
        targetCustomer: idea.targetCustomer || idea.targetAudience || 'Target customer not specified',
        validationQuestion: idea.validationQuestion || 'Would you use this solution?',
        skillsRequired: Array.isArray(idea.skillsRequired)
          ? idea.skillsRequired.filter((skill: any) => typeof skill === 'string' && skill.trim().length > 0)
          : typeof idea.skillsRequired === 'string' && idea.skillsRequired.trim().length > 0
            ? [idea.skillsRequired.trim()]
            : [],
        technicalFeasibility: this.validateFeasibility(idea.technicalFeasibility),
        estimatedDevelopment: idea.estimatedDevelopment || '2-4 weeks'
      }));
    } catch (error) {
      console.error('Failed to parse business ideas:', error);
      console.error('Response excerpt:', response.substring(0, 500));
      throw new Error('Failed to parse AI response');
    }
  }

  /**
   * Parse validation questions from AI response
   */
  static parseValidationQuestions(response: string): SurveyQuestion[] {
    try {
      const jsonMatch = response.match(/\[[\s\S]*\]/);
      if (!jsonMatch) {
        throw new Error('No JSON array found in response');
      }

      const questions = JSON.parse(jsonMatch[0]);
      return questions.map((q: any, index: number) => ({
        id: q.id || `q${index + 1}`,
        text: q.text || `Question ${index + 1}`,
        type: q.type || 'multiple-choice',
        options: q.options || ['Yes', 'No', 'Maybe'],
        required: q.required !== false
      }));
    } catch (error) {
      console.error('Failed to parse validation questions:', error);
      throw new Error('Failed to parse validation questions');
    }
  }

  /**
   * Parse task checklist from AI response
   */
  static parseTaskChecklist(response: string): TaskItem[] {
    try {
      const jsonMatch = response.match(/\[[\s\S]*\]/);
      if (!jsonMatch) {
        throw new Error('No JSON array found in response');
      }

      const tasks = JSON.parse(jsonMatch[0]);
      return tasks.map((task: any, index: number) => ({
        id: task.id || `task${index + 1}`,
        title: task.title || `Task ${index + 1}`,
        description: task.description || 'Task description not provided',
        completed: false,
        priority: this.validatePriority(task.priority)
      }));
    } catch (error) {
      console.error('Failed to parse task checklist:', error);
      throw new Error('Failed to parse task checklist');
    }
  }

  /**
   * Validate technical feasibility value
   */
  private static validateFeasibility(feasibility: any): 'low' | 'medium' | 'high' {
    const normalized = String(feasibility).toLowerCase();
    if (['high', 'medium', 'low'].includes(normalized)) {
      return normalized as 'low' | 'medium' | 'high';
    }
    return 'medium';
  }

  /**
   * Validate priority value
   */
  private static validatePriority(priority: any): 'High' | 'Medium' | 'Low' {
    if (['High', 'Medium', 'Low'].includes(priority)) {
      return priority;
    }
    return 'Medium';
  }
}
